import { getSupabaseClient } from "@/lib/supabase/client";
import type { AdminListParams, AdminListResponse } from "./types";


export const API_BASE = `${import.meta.env.BASE_URL || "/"}api`;


/**
 * Task #3155 — sentinel for the server-side admin MFA gate.
 *
 * `requireAdmin` answers admins who haven't satisfied two-factor (no
 * verified TOTP factor, or an aal1 session) with `403 mfa_required`.
 * That is an expected auth-envelope response, not a generic failure, so
 * we surface it as a typed error the UI can recognise and swallow
 * quietly (the `AdminMfaGate` already shows the enrolment wall) instead
 * of letting it bubble up as a noisy uncaught error to the tracker.
 */
export class MfaRequiredError extends Error {
  readonly reason?: string;
  constructor(reason?: string) {
    super("mfa_required");
    this.name = "MfaRequiredError";
    this.reason = reason;
  }
}

export class AdminApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly stage?: string;
  readonly requestId?: string;

  constructor(message: string, context: { status: number; code?: string; stage?: string; requestId?: string }) {
    super(message);
    this.name = "AdminApiError";
    this.status = context.status;
    this.code = context.code;
    this.stage = context.stage;
    this.requestId = context.requestId;
  }
}


export function authHeaders(token: string): HeadersInit {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}


// Task #4583 — admin calls (notably the impersonate step-up retry, which
// fires into a burst of traffic as the app reloads as the impersonated
// user) intermittently catch a transient `429` emitted upstream by the
// proxy/infra layer rather than by any server-side limiter. Absorb those
// with a small bounded retry loop instead of dead-ending the admin on the
// first non-OK response.
const MAX_429_RETRIES = 3;
// Cap any single wait so a long `Retry-After` (e.g. the hour-long PII
// distinct-lookup cap) can never hang the UI; we still honour shorter
// hints and fall back to a capped exponential backoff otherwise.
const MAX_429_BACKOFF_MS = 8_000;

// Task #5147 — momentary infra/database blips surface as `502 Bad
// Gateway` / `503 Service Unavailable` / `504 Gateway Timeout` (the
// server now emits a clean recoverable `503 database_unavailable` for a
// brief database outage). Retry these a small, bounded number of times
// with a short capped backoff — mirroring the 429 path — so a one- or
// two-second blip is smoothed over transparently instead of dead-ending
// the admin on the first transient failure.
const RETRYABLE_SERVER_STATUSES = new Set([502, 503, 504]);
const MAX_SERVER_ERROR_RETRIES = 2;
const MAX_SERVER_ERROR_BACKOFF_MS = 4_000;

// Friendly copy shown once the transient-error retries are exhausted, so
// the admin sees an actionable "try again" hint rather than a raw
// "API error 503". Exported so callers that want to show messaging that
// tells a transient blip apart from a real failure (Task #8496) can match
// on the exact string instead of re-deriving their own status-code check.
export const TEMPORARILY_UNAVAILABLE_MESSAGE =
  "The service is temporarily unavailable. Please try again in a moment.";

/**
 * Parse an HTTP `Retry-After` header into a millisecond delay. Supports
 * both the delta-seconds form ("30") and the HTTP-date form. Returns
 * `null` when the header is absent or unparseable so the caller can fall
 * back to exponential backoff.
 */
function parseRetryAfterMs(header: string | null): number | null {
  if (!header) return null;
  const trimmed = header.trim();
  if (trimmed === "") return null;
  const seconds = Number(trimmed);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const dateMs = Date.parse(trimmed);
  if (!Number.isNaN(dateMs)) return Math.max(0, dateMs - Date.now());
  return null;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * `fetch` wrapper that retries a bounded number of times on transient
 * failures:
 *  - `429 Too Many Requests` (up to `MAX_429_RETRIES`), honouring a
 *    `Retry-After` header when present.
 *  - transient server errors `502`/`503`/`504` (up to
 *    `MAX_SERVER_ERROR_RETRIES`), to smooth over momentary infra/database
 *    blips such as the server's recoverable `503 database_unavailable`.
 *
 * Each class keeps its own attempt counter and capped exponential
 * backoff, and every wait is clamped so the admin UI never stalls. Any
 * other response (including the expected `401 step_up_required` / `403
 * mfa_required` auth envelopes) is returned immediately, unchanged.
 */
async function fetchWithRetry(input: string, init: RequestInit, retry = true): Promise<Response> {
  let rateLimitAttempt = 0;
  let serverErrorAttempt = 0;
  for (;;) {
    const res = await fetch(input, init);
    // Non-idempotent status decisions opt out: an ambiguous gateway response
    // must prompt a detail refresh, never silently replay a human decision.
    if (!retry) return res;
    if (res.status === 429 && rateLimitAttempt < MAX_429_RETRIES) {
      const retryAfterMs = parseRetryAfterMs(res.headers.get("Retry-After"));
      const backoffMs = 500 * 2 ** rateLimitAttempt;
      const waitMs = Math.min(MAX_429_BACKOFF_MS, retryAfterMs ?? backoffMs);
      rateLimitAttempt += 1;
      await sleep(waitMs);
      continue;
    }
    if (
      RETRYABLE_SERVER_STATUSES.has(res.status) &&
      serverErrorAttempt < MAX_SERVER_ERROR_RETRIES
    ) {
      const retryAfterMs = parseRetryAfterMs(res.headers.get("Retry-After"));
      const backoffMs = 500 * 2 ** serverErrorAttempt;
      const waitMs = Math.min(MAX_SERVER_ERROR_BACKOFF_MS, retryAfterMs ?? backoffMs);
      serverErrorAttempt += 1;
      await sleep(waitMs);
      continue;
    }
    return res;
  }
}

/**
 * Build the error thrown for a non-OK admin response. Centralised so the
 * primary call and both auth-retry paths surface failures consistently:
 *  - `403 mfa_required` → typed `MfaRequiredError` (an expected auth
 *    envelope callers can swallow quietly).
 *  - exhausted transient server errors (`502`/`503`/`504`) → a friendly
 *    "temporarily unavailable" message instead of a raw `API error 503`.
 *  - everything else → the server message/error, falling back to
 *    `API error <status>`.
 */
async function throwForResponse(res: Response): Promise<never> {
  const body = await res.json().catch(() => ({}));
  if (res.status === 403 && body.error === "mfa_required") {
    throw new MfaRequiredError(typeof body.reason === "string" ? body.reason : undefined);
  }
  if (RETRYABLE_SERVER_STATUSES.has(res.status)) {
    const safeDecisionError = ["listing_status_storage_failed", "listing_review_storage_failed"].includes(body.code);
    throw new AdminApiError(safeDecisionError ? body.error : TEMPORARILY_UNAVAILABLE_MESSAGE, {
      status: res.status,
      code: typeof body.error === "string" ? body.error : undefined,
      stage: typeof body.stage === "string" ? body.stage : undefined,
      requestId: typeof body.requestId === "string" ? body.requestId : undefined,
    });
  }
  throw new AdminApiError(body.message || body.reason || body.error || `API error ${res.status}`, {
    status: res.status,
    code: typeof body.code === "string" ? body.code : typeof body.error === "string" ? body.error : undefined,
    stage: typeof body.stage === "string" ? body.stage : undefined,
    requestId: typeof body.requestId === "string" ? body.requestId : undefined,
  });
}


export async function getFreshToken(fallbackToken: string): Promise<string> {
  if (!fallbackToken) {
    const sb = getSupabaseClient();
    if (sb) {
      const { data } = await sb.auth.getSession();
      if (data.session?.access_token) return data.session.access_token;
    }
  }
  return fallbackToken;
}


export async function adminFetch<T>(path: string, token: string, init?: RequestInit, retry = true): Promise<T> {
  const freshToken = await getFreshToken(token);
  const res = await fetchWithRetry(`${API_BASE}${path}`, {
    ...init,
    headers: { ...authHeaders(freshToken), ...init?.headers },
  }, retry);
  if (res.status === 401) {
    // Task #3089 — destructive admin routes return 401 `step_up_required`
    // when the admin's last strong-auth stamp is stale. Pop the same
    // re-auth modal users already see, then retry exactly once. We must
    // peek the body BEFORE the session-refresh branch below, otherwise a
    // valid token + stale stamp would be misread as an expired session
    // and the user would silently sign out instead of being prompted.
    const cloned = res.clone();
    const peeked = await cloned.json().catch(() => ({} as { error?: string; action?: string }));
    if (peeked && peeked.error === "step_up_required" && typeof peeked.action === "string") {
      const { isSensitiveAction } = await import("@workspace/api-zod");
      if (isSensitiveAction(peeked.action)) {
        const { STEP_UP_EVENT } = await import("@/lib/step-up-event");
        await new Promise<void>((resolve, reject) => {
          window.dispatchEvent(new CustomEvent(STEP_UP_EVENT, {
            detail: { action: peeked.action, resolve, reject },
          }));
        });
        let retryTok = freshToken;
        try {
          const sbRetry = getSupabaseClient();
          if (sbRetry) {
            const { data: retrySession } = await sbRetry.auth.getSession();
            if (retrySession.session?.access_token) retryTok = retrySession.session.access_token;
          }
        } catch {
          // getSession() failed — fall back to the pre-step-up token
        }
        const retryRes = await fetchWithRetry(`${API_BASE}${path}`, {
          ...init,
          headers: { ...authHeaders(retryTok), ...init?.headers },
        }, retry);
        if (!retryRes.ok) await throwForResponse(retryRes);
        return retryRes.json();
      }
    }
    const sb = getSupabaseClient();
    if (sb) {
      const { data } = await sb.auth.refreshSession();
      if (data.session?.access_token && data.session.access_token !== freshToken) {
        const retryRes = await fetchWithRetry(`${API_BASE}${path}`, {
          ...init,
          headers: { ...authHeaders(data.session.access_token), ...init?.headers },
        }, retry);
        if (!retryRes.ok) await throwForResponse(retryRes);
        return retryRes.json();
      }
    }
  }
  if (!res.ok) await throwForResponse(res);
  return res.json();
}


export function buildListParams(params: AdminListParams): URLSearchParams {
  const sp = new URLSearchParams();
  if (params.q) sp.set("q", params.q);
  if (params.page) sp.set("page", String(params.page));
  if (params.pageSize) sp.set("pageSize", String(params.pageSize));
  if (params.sort) sp.set("sort", params.sort);
  if (params.status) sp.set("status", params.status);
  if (params.filter) sp.set("filter", params.filter);
  if (params.classification) sp.set("classification", params.classification);
  if (params.lifecycle_state) sp.set("lifecycle_state", params.lifecycle_state);
  if (params.lifecycle_reason) sp.set("lifecycle_reason", params.lifecycle_reason);
  return sp;
}


export function normalizeList<T>(raw: { rows?: T[]; total?: number; page?: number; pageSize?: number; limit?: number; truncated?: boolean } & Record<string, unknown>, legacyKey: string): AdminListResponse<T> {
  const rows = (raw.rows ?? raw[legacyKey] ?? []) as T[];
  return {
    rows,
    total: raw.total ?? 0,
    page: raw.page ?? 1,
    pageSize: raw.pageSize ?? raw.limit ?? rows.length,
    truncated: raw.truncated === true ? true : undefined,
    // Task #5537 — pass the held-edit count through so the listings view can
    // badge the "Pending Edits" queue tab regardless of selected status.
    held_edits_count: typeof raw.held_edits_count === "number" ? raw.held_edits_count : undefined,
  };
}
